import React from 'react'
import { useDispatch } from 'react-redux'
import {
  Text as MedkitText,
  LinkButton,
  EditorialLayout,
} from '@babylon/medkit'
import { useChangeLocale, useIntl, useFormatMessage } from '@babylon/intl'

import { Text, Link } from '@babylon/web-platform-ui'
import { setLocale } from '@/redux/userConfig/actions'
import { useTrackingScreenTitle } from '@/hooks'
import { trackTealiumEvent } from '@/tracking'
import { useUserId } from '@/redux/userProfile/selectors'
import messages from './messages'
import styles from './LanguageSwitcher.module.scss'

type LanguageSwitcherProps = {
  languages: string[]
  onChange?: (newLocale: string) => void
  useDnaV2Design: Boolean
}

const LanguageSwitcher = ({
  languages,
  onChange,
  useDnaV2Design,
}: LanguageSwitcherProps) => {
  const changeLocale = useChangeLocale()
  const intl = useIntl()
  const translate = useFormatMessage()
  const dispatch = useDispatch()
  const { locale } = intl
  const stepTrackingName = useTrackingScreenTitle()
  const userId = useUserId()

  const onLocaleChange = (newLocale: string) => {
    changeLocale(newLocale)
    dispatch(setLocale(newLocale))

    if (onChange) {
      onChange(newLocale)
    }

    trackTealiumEvent({
      screen_title: stepTrackingName,
      eventCategory: 'signup',
      eventAction: 'select language',
      eventLabel: newLocale.toLowerCase(),
      recipientId: userId,
      tealium_event: 'select language',
    })
  }

  // No need to display anything if we have only one language
  if (languages.length === 1) {
    return null
  }

  // Exclude the current language from the list
  const availableLanguages = languages.filter((lang) => lang !== locale)

  if (useDnaV2Design) {
    return (
      <Text as="p" marginTop="12">
        {translate(messages.continueIn)}{' '}
        {availableLanguages.map((lang) => (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link
            data-testid={`language-${lang}`}
            key={lang}
            onClick={() => onLocaleChange(lang)}
            as="button"
            type="button"
          >
            {translate(messages[lang as keyof typeof messages])}
          </Link>
        ))}
      </Text>
    )
  }

  return (
    <EditorialLayout>
      <MedkitText variant="body">
        {translate(messages.continueIn)}
        {availableLanguages.map((lang) => (
          <LinkButton
            data-testid={`language-${lang}`}
            className={styles.languageSwitcherLanguage}
            key={lang}
            onClick={() => onLocaleChange(lang)}
          >
            {translate(messages[lang as keyof typeof messages])}
          </LinkButton>
        ))}
      </MedkitText>
    </EditorialLayout>
  )
}

export default LanguageSwitcher
